import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LoadState, TwoFactorAuthType } from '../common/enums.mjs';
import { useNavigateSmart } from '../common/hooks';
import './Setup2FA.css';
import { Route, Routes, useLocation } from 'react-router-dom';
import { LocaleContext, LoginContext, OverlayContext, StateContext } from '../common/App';
import { Authenticator } from '../common/Authenticator';
import { TwoFactorAuthActivation } from '../common/TwoFactorAuthActivation';
import { ForwardsBackwardsBar, HeaderWithButton, LinkSmart, NavigateSmart, RadioCheckButton, TelNoInputNew } from '../common/Common';
import { postWithCsrf } from '../common/util';

export const Setup2FA = ({}) => {
    const navigate = useNavigateSmart();
    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);
    const state = useContext(StateContext);
    const overlay = useContext(OverlayContext);

    const location = useLocation();

    const [tabBoxWidth, setTabBoxWidth] = useState(0);
    const [endDummyHeight, setEndDummyHeight] = useState(0);

    const [twoFactorType, setTwoFactorType] = useState(TwoFactorAuthType.NONE);
    const [twoFactorPhoneNo, setTwoFactorPhoneNo] = useState("");

    const [loadState, setLoadState] = useState(LoadState.NONE);

    const setupNavRef = useRef();
    const navBoxRef = useRef();


    // FUNCTIONS:

    function handleAuthenticated() {
        navigate("/dashboard/");
    }

    function handleMobileFowardsClick() {
        try {
            postWithCsrf("/user/setup_two_factor_auth_mobile", JSON.stringify({"phone_number": twoFactorPhoneNo}), state).then(response => {
                if(response.ok) {
                    return response.json().then(json => {
                        setLoadState(LoadState.LOAD_SUCCESS);
                        login.setNextSmsCountS(Math.round(json.next_sms_count_s));
                        navigate("authenticator_input/");
                    });
                }
                else {
                    if(response.status == 522) {
                        return response.text().then((text) => {
                            overlay.setErrorPopupInfo({"title": locale.msgs.TWO_FACTOR_AUTH_MOBILE_LIMIT_ERROR,
                                    "text": locale.msgs.TWO_FACTOR_AUTH_MOBILE_LOCKED_CODE_STILL_VALID_ACTIVATION.replace("{{phone_number}}", text)});
                            overlay.setErrorPopupDisplayed(true);

                            throw new Error("Response code: " + response.status);
                        });
                    }
                    else if(response.status == 523) {
                        return response.text().then((text) => {
                            overlay.setErrorPopupInfo({"title": locale.msgs.TWO_FACTOR_AUTH_MOBILE_LIMIT_ERROR,
                                    "text": locale.msgs.TWO_FACTOR_AUTH_MOBILE_LOCKED_CODE_EXPIRED_ACTIVATION.replace("{{hours}}", text)});
                            overlay.setErrorPopupDisplayed(true);

                            throw new Error("Response code: " + response.status);
                        });
                    }
                    else if(response.status == 528) {
                        return response.text().then((text) => {
                            overlay.setErrorPopupInfo({"title": locale.msgs.PHONE_NO_VERFICATION_FAILED,
                                    "text": locale.msgs.PHONE_NO_VERFICATION_FAILED_DESCR});
                            overlay.setErrorPopupDisplayed(true);

                            throw new Error("Response code: " + response.status);
                        });
                    }
                    else {
                        throw new Error("Response code: " + response.status);
                    }
                }
            }).catch(error => {
                setLoadState(LoadState.LOAD_ERROR);
                console.log(error);
            });

            // Calling setLoadState() after postWithCsrf() as it could throw an exception
            setLoadState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }


    // EFFECTS:

    useLayoutEffect(() => {
        function perform() {
            if(navBoxRef.current) {
                setTabBoxWidth(setupNavRef.current.clientWidth - 1);
                setEndDummyHeight(navBoxRef.current.clientHeight);
            }
        }

        perform();

        window.addEventListener("resize", perform);
        return () => window.removeEventListener("resize", perform);
    });

    useEffect(() => {
        setLoadState(LoadState.NONE); // Clear errors on actions
    }, [twoFactorType, twoFactorPhoneNo]);


    // RENDER:

    if(login.twoFactorAuthEnabled) {
        return <NavigateSmart to={"/dashboard/"} />;
    }

    let backwardsVisible, forwardsVisible;
    let backwardsClickable, forwardsClickable;
    let forwardsFunc, backwardsFunc;

    if(location.pathname.endsWith("select/")) {
        forwardsVisible = true;
        backwardsVisible = false;

        if(twoFactorType === TwoFactorAuthType.MOBILE) {
            if(twoFactorPhoneNo) {
                forwardsClickable = true;
            }
            else {
                forwardsClickable = false;
            }
            
            forwardsFunc = handleMobileFowardsClick;
        }
        else if(twoFactorType === TwoFactorAuthType.APP) {
            forwardsClickable = true;
            forwardsFunc = () => { navigate("authenticator/") };
        }
        else {
            forwardsClickable = false;
        }    
    }
    else if(location.pathname.endsWith("authenticator/")) {
        forwardsVisible = true;
        backwardsVisible = true;

        forwardsClickable = true;
        backwardsClickable = true;

        forwardsFunc = () => { navigate("authenticator_input/") };
        backwardsFunc = () => { navigate("select/") };

        if(twoFactorType !== TwoFactorAuthType.APP) {
            return <NavigateSmart to={"select/"} />;
        }
    }
    else if(location.pathname.endsWith("authenticator_input/")) {
        forwardsVisible = false;
        backwardsVisible = true;

        backwardsClickable = true;

        if(twoFactorType === TwoFactorAuthType.MOBILE) {
            backwardsFunc = () => { navigate("select/") };
        }
        else if(twoFactorType === TwoFactorAuthType.APP) {
            backwardsFunc = () => { navigate("authenticator/") };
        }
        else {
            return <NavigateSmart to={"select/"} />;
        }
    }

    return(
        <div ref={setupNavRef} className="setup_2fa">
            <HeaderWithButton closeBtn={true} onClick={() => navigate("/")} onHeaderClick={() => navigate("/")}
                    langSelection={true} />

            <div className="side_padding margin_to_header">
                <Routes>                    
                    <Route path="select/" element={
                        <SensitiveData twoFactorType={twoFactorType} setTwoFactorType={setTwoFactorType}
                                    twoFactorPhoneNo={twoFactorPhoneNo} setTwoFactorPhoneNo={setTwoFactorPhoneNo} />
                    }></Route>

                    <Route path="authenticator/" element={<Authenticator management={true} twoFactorType={twoFactorType} />}></Route>
                    <Route path="authenticator_input/" element={<TwoFactorAuthActivation onAuthenticated={handleAuthenticated}
                            twoFactorType={twoFactorType} twoFactorPhoneNo={twoFactorPhoneNo} />}></Route>
                    <Route path="*" element={<NavigateSmart to="select/"/>}></Route>
                </Routes>

                { window.innerWidth < 450 ?
                    <div style={{width: "100%", height: endDummyHeight}}></div>
                :
                    null
                }
            </div>

            <div ref={navBoxRef} className="setup_2fa_nav" style={{width: tabBoxWidth}}>
                <ForwardsBackwardsBar
                        backwardsVisible={backwardsVisible}
                        forwardsVisible={forwardsVisible}
                        backwardsClickable={backwardsClickable} forwardsClickable={forwardsClickable}
                        onBackwards={backwardsFunc} onForwards={forwardsFunc}
                        backwardsText={locale.msgs.BACK} forwardsText={locale.msgs.CONTINUE}
                        forwardsBig={false} forwardsLoadState={loadState}
                />
            </div>
        
        </div>
    );
}

function SensitiveData(props) {
    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);

    if(login.twoFactorAuthEnabled) {
        return <NavigateSmart to="/collect_data/how_it_works/"/>;  // Skip the 2fa activation
    }
    // Stay here

    return (
        <div id="sensitive_data">
            <div className="head">{locale.msgs.SENSITIVE_DATA}</div>
            
            <div className="text">
                <span dangerouslySetInnerHTML={{__html: locale.msgs.SENSITIVE_DATA_DESCR}}></span>
                <LinkSmart className="link_2" to={"/privacy_policy/"} target="_blank">{locale.msgs.PRIVACY_POLICY}</LinkSmart>
                .
            </div>

            <div id="sensitive_data_authenticator_box">
                <TwoFactorSelect twoFactorType={props.twoFactorType} setTwoFactorType={props.setTwoFactorType}
                        twoFactorPhoneNo={props.twoFactorPhoneNo} setTwoFactorPhoneNo={props.setTwoFactorPhoneNo} />
            </div>

        </div>
    );
}

const TwoFactorSelect = ({twoFactorType, setTwoFactorType, twoFactorPhoneNo, setTwoFactorPhoneNo}) => {
    const locale = useContext(LocaleContext);

    return (
        <div className="two_factor_select">
            <div className="head text_center">{locale.msgs.TWO_FACTOR_YOU_NEED}</div>
            <div className="head">{locale.msgs.TWO_FACTOR_APP_NAME}</div>
            <div className="text" dangerouslySetInnerHTML={{__html: locale.msgs.TWO_FACTOR_APP_DESCR}}></div>

            <div className="two_factor_select_btn_box">
                <div className="text" dangerouslySetInnerHTML={{__html: locale.msgs.WHICH_TWO_FACTOR}}></div>
                
                <RadioCheckButton type="radio" text={locale.msgs.AUTH_APP} id="two_fac_1" name="two_fac_1"
                        checked={twoFactorType === TwoFactorAuthType.APP}
                        onChange={(e) => {
                            if(e.target.checked) {
                                setTwoFactorType(TwoFactorAuthType.APP);
                            }
                        }}
                />

                <RadioCheckButton type="radio" text={locale.msgs.MOBILE_DEVICE_SMS} id="two_fac_2" name="two_fac_2"
                        checked={twoFactorType === TwoFactorAuthType.MOBILE}
                        onChange={(e) => {
                            if(e.target.checked) {
                                setTwoFactorType(TwoFactorAuthType.MOBILE);
                            }
                        }}
                />

                { twoFactorType === TwoFactorAuthType.MOBILE ?
                    <div className="specific_box">
                        <TelNoInputNew value={twoFactorPhoneNo} onChange={setTwoFactorPhoneNo}
                                showPlaceholderIfNotEmpty={false} />
                    </div>
                :
                    null
                }
            </div>
        </div>
    );
}
